<template>
  <div :key="resetKey" class="my-10">
    <h1 class="font-bold text-2xl">{{ $t('nav.addPublication') }}</h1>
    <div class="mt-5 bg-white rounded p-5">
      <div class="mb-4 flex justify-end">
        <button
          @click="clearAll"
          class="bg-accent-color text-white px-10 py-2 rounded hover:bg-main-color"
        >
          {{ $t('page.clear') }}
        </button>
      </div>
      <div class="grid grid-cols-3 gap-4 max-md:grid-cols-2 max-sm:grid-cols-1">
        <TextField
          v-if="categoryDetail?.has_name"
          ref="title"
          :required="categoryDetail?.is_name_required"
          name="name"
          :label="$t('page.name')"
        ></TextField>

        <template v-if="ogPropertiesReady">
          <component
            v-for="(field, index) in ogProperties"
            :key="index"
            :is="getComponent(field.data_type)"
            :ref="field.id"
            :field="field"
            :name="field.name"
            :label="field.name"
            :required="field.is_required"
            :property="field.id"
            :options="field.values"
          >
          </component>
        </template>
        <TextField
          ref="phone"
          placeholder="+993"
          :required="true"
          name="phone"
          :label="$t('form.phone')"
        ></TextField>
        <NumberField
          v-if="categoryDetail?.has_price"
          ref="price"
          :required="categoryDetail?.is_price_required"
          name="price"
          :label="$t('page.price')"
        ></NumberField>
        <LocationNestedSelectField
          v-if="categoryDetail?.has_address"
          ref="address"
          name="district"
          :label="$t('form.address')"
          :required="categoryDetail?.is_address_required"
          :options="publicationLocations"
        ></LocationNestedSelectField>
      </div>
      <div class="flex flex-col mt-4">
        <div class="flex flex-col gap-2">
          <label for="message" class="font-semibold"
            >{{ $t('page.info') }}<span class="text-red-500">*</span></label
          >
          <Field
            v-model="formData.description"
            as="textarea"
            name="description"
            id="description"
            :maxlength="$store.setting.max_publication_description_length"
            cols="30"
            rows="6"
            class="w-full rounded border px-8 py-5 placeholder-[#5F5F5F] resize-none max-md:py-4"
          >
          </Field>
          <ErrorMessage name="description" class="text-red-500"></ErrorMessage>
        </div>
        <!-- THUMBNAIL PICTURE (строго отдельно от фото/видео) -->
        <div v-if="categoryDetail?.has_thumbnail_picture" class="w-full flex flex-col gap-2 mt-4">
          <p class="font-semibold">
            {{ $t('page.thumbnailPicture') }}
            <span class="text-red-500" v-show="categoryDetail?.is_thumbnail_picture_required"
              >*</span
            >
          </p>

          <div class="flex items-center gap-4">
            <input
              ref="thumbnailInput"
              class="hidden"
              type="file"
              accept=".jpg,.jpeg,.png"
              @change="onThumbnailChange"
            />

            <button
              type="button"
              class="flex gap-3 items-center bg-ml-secondary text-main-color rounded-md cursor-pointer py-3 px-5 hover:bg-ml-main active:bg-ml-main"
              @click="$refs.thumbnailInput.click()"
              :disabled="loadingThumbnail"
            >
              <IconUpload />
              {{ $t('page.uploadImage') }}
            </button>

            <InlineLoader class="!w-8 !h-8" :showLoader="loadingThumbnail"></InlineLoader>

            <button
              v-if="thumbnailId"
              type="button"
              class="bg-[#EB5757] text-white px-4 py-2 rounded hover:opacity-90"
              @click="removeThumbnail"
            >
              {{ $t('page.remove') }}
            </button>
          </div>

          <div v-if="thumbnailPreview" class="mt-2">
            <img
              :src="thumbnailPreview"
              class="h-28 w-48 object-contain rounded border"
              alt="thumb-preview"
            />
          </div>
        </div>
        <template v-if="categoryDetail?.has_image">
          <div class="w-full flex flex-col gap-2 mt-4">
            <p for="additionalinfo" class="font-semibold">
              {{ $t('page.uploadImage') }} ({{ imageFiles.length }}/{{ getMaxImageCount }})
              <span class="text-red-500" v-show="categoryDetail.is_image_required">*</span>
            </p>
            <div
              @dragover="dragoverImage"
              @dragleave="dragleaveImage"
              @drop="dropImage"
              class="flex justify-center items-center flex-col border-4 border-dashed border-ml-input-100 p-20"
              :class="{ 'bg-ml-input-200': isDraggingImage }"
            >
              <input
                :disabled="imageFiles.length >= getMaxImageCount"
                ref="imageFile"
                name="files"
                class="hidden"
                type="file"
                multiple
                id="imageFileUpload"
                @change="onImageFileChange"
                accept=".jpg,.jpeg,.png"
              />
              <InlineLoader class="!w-8 !h-8" :showLoader="loadingImage"></InlineLoader>
              <label
                v-if="!loadingImage"
                class="flex gap-3 items-center bg-ml-secondary text-main-color rounded-md cursor-pointer py-3 px-5 hover:bg-ml-main active:bg-ml-main"
                for="imageFileUpload"
              >
                <template v-if="imageFiles.length < getMaxImageCount">
                  <IconUpload></IconUpload>{{ $t('page.uploadImage') }}
                </template>
                <template v-else>
                  <p class="text-blue-500">
                    {{ $t('page.maxImage') }}
                  </p>
                </template>
              </label>
            </div>
          </div>
        </template>
        <div
          class="grid grid-cols-3 gap-5 mt-4 max-lg:grid-cols-2 max-md:grid-cols-1"
          v-if="imageFiles.length"
        >
          <div
            v-for="(file, idx) in imageFiles"
            :key="(file.detail?.name || file.detail?.id || file.path) + '-' + idx"
            class="w-full flex items-center justify-between p-2 gap-4 border-t border-b"
          >
            <img :src="file.path" alt="uploaded-image" class="h-20 w-32 object-fit" />
            <div class="flex items-center gap-2">
              <input
                type="radio"
                class="scale-150"
                name="main-image"
                :id="(file.detail?.name || file.detail?.id || file.path || idx) + '-' + idx"
              />
              <label :for="(file.detail?.name || file.detail?.id || file.path || idx) + '-' + idx">
                {{ file.detail?.name || file.detail?.id || 'file' }}
              </label>
            </div>
            <button
              class="ml-3 mt-1 bg-[#EB5757] p-2.5 rounded"
              type="button"
              @click="removeImage(idx)"
              title="Remove file"
            >
              <IconTrash></IconTrash>
            </button>
          </div>
        </div>
        <div class="w-full flex flex-col gap-2 mt-4">
          <p class="font-semibold">
            {{ $t('page.uploadVideo') }} ({{ videoFiles.length }}/{{ getMaxVideoCount }})
          </p>
          <div
            @dragover="dragoverVideo"
            @dragleave="dragleaveVideo"
            @drop="dropVideo"
            class="flex justify-center items-center flex-col border-4 border-dashed border-ml-input-100 p-20"
            :class="{ 'bg-ml-input-200': isDraggingVideo }"
          >
            <input
              :disabled="videoFiles.length >= getMaxVideoCount"
              ref="videoFile"
              name="files"
              class="hidden"
              type="file"
              multiple
              id="videoFileUpload"
              @change="onVideoFileChange"
              accept=".mp4,.mov,.avi,.mkv,.webm"
            />
            <InlineLoader class="!w-8 !h-8" :showLoader="loadingVideo"></InlineLoader>
            <label
              v-if="!loadingVideo"
              class="flex gap-3 items-center bg-ml-secondary text-main-color rounded-md cursor-pointer py-3 px-5 hover:bg-ml-main active:bg-ml-main"
              for="videoFileUpload"
            >
              <template v-if="videoFiles.length < getMaxVideoCount">
                <IconUpload></IconUpload>{{ $t('page.uploadVideo') }}
              </template>
              <template v-else>
                <p class="text-blue-500">
                  {{ $t('page.maxVideo') }}
                </p>
              </template>
            </label>
          </div>
        </div>

        <div
          class="grid grid-cols-3 gap-5 mt-4 max-lg:grid-cols-2 max-md:grid-cols-1"
          v-if="videoFiles.length"
        >
          <div
            v-for="(file, idx) in videoFiles"
            :key="(file.detail?.name || file.detail?.id || file.path) + '-' + idx"
            class="w-full flex items-center justify-between p-2 gap-4 border-t border-b"
          >
            <IconMedia class="w-20 h-20"></IconMedia>
            <div class="flex items-center gap-2">
              <!-- <input type="radio" class="scale-150" name="main-image" :id="file.detail.name"> -->
              <label>
                {{ file.detail?.name || file.detail?.id || 'file' }}
              </label>
            </div>
            <button
              class="ml-3 mt-1 bg-[#EB5757] p-2.5 rounded"
              type="button"
              @click="removeVideo(idx)"
              title="Remove file"
            >
              <IconTrash></IconTrash>
            </button>
          </div>
        </div>
      </div>
      <div class="my-4">
        <div class="grid grid-cols-3 my-6">
          <SwitchField
            v-if="categoryDetail?.on_credit"
            :required="false"
            ref="on_credit"
            :label="$t('page.onCredit')"
            name="on_credit"
          ></SwitchField>
          <SwitchField
            v-if="categoryDetail?.on_exchange"
            :required="false"
            ref="on_exchange"
            :label="$t('page.onExchange')"
            name="on_exchange"
          ></SwitchField>
          <SwitchField
            :required="false"
            ref="allow_comments"
            :label="$t('page.allowComments')"
            name="allow_comments"
          ></SwitchField>
        </div>
        <div class="flex flex-col my-6">
          <div class="flex gap-2">
            <input type="checkbox" class="scale-150" name="terms" id="terms" v-model="terms" />
            <label for="terms">
              <router-link class="text-accent-color" :to="{ name: 'policy' }">
                {{ $t('page.readAddPublicationTerms') }}
              </router-link>
            </label>
          </div>
          <span class="text-danger text-red-500" v-if="termsErr">{{ termsErr }}</span>
        </div>
      </div>
      <div>
        <SubmitBtn @click="onSubmitForm">{{ $t('nav.addPublication') }}</SubmitBtn>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import TextField from '@cmp/TextField.vue';
import NumberField from '@cmp/NumberField.vue';
import SwitchField from '@cmp/SwitchField.vue';
import SelectField from '@cmp/SelectField.vue';
import MultiSelectField from '@cmp/MultiSelectField.vue';
import NestedSelectField from '@cmp/NestedSelectField.vue';
import MultiNestedSelectField from '@cmp/MultiNestedSelectField.vue';
import NestedMultiSelectField from '@cmp/NestedMultiSelectField.vue';
import LocationNestedSelectField from '@cmp/LocationNestedSelectField.vue';
import Multiselect from '@vueform/multiselect';
import IconUpload from '@icons/IconUpload.vue';
import IconTrash from '@icons/IconTrash.vue';
import IconMedia from '@icons/IconMedia.vue';
import InlineLoader from '@cmp/InlineLoader.vue';
import { Field, Form, ErrorMessage } from 'vee-validate';
import { useField } from 'vee-validate';
import * as yup from 'yup';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { i18n } from '@/i18n';
export default {
  name: 'addPublication',

  components: {
    TextField,
    SwitchField,
    MultiSelectField,
    MultiNestedSelectField,
    LocationNestedSelectField,
    SelectField,
    NumberField,
    Multiselect,
    NestedSelectField,
    Field,
    Form,
    ErrorMessage,
    IconUpload,
    InlineLoader,
    IconTrash,
    IconMedia,
    NestedMultiSelectField,
  },
  setup() {
    const { t } = useI18n();
    const childComponents = ref([]);
    const on_credit = ref();
    const on_exchange = ref();
    const allow_comments = ref();
    const price = ref();
    const phone = ref();
    const title = ref();
    const address = ref();

    const termsRules = yup
      .bool()
      .oneOf([true], t('warning.REQUIRED_MESSAGE'))
      .required(t('warning.REQUIRED_MESSAGE'));
    const {
      meta: termsMeta,
      value: terms,
      errorMessage: termsErr,
      validate: termsValidate,
    } = useField('terms', termsRules, { initialValue: true });
    return {
      termsMeta,
      terms,
      termsErr,
      termsValidate,
      childComponents,
      on_credit,
      on_exchange,
      allow_comments,
      price,
      phone,
      title,
      address,
    };
  },
  data() {
    return {
      thumbnailFile: null,
      thumbnailPreview: null,
      thumbnailId: null,
      loadingThumbnail: false,
      loading: false,
      loadingImage: false,
      loadingVideo: false,
      currentCategory: null,
      ogProperties: [],
      properties: [],
      publicationLocations: [],
      isDraggingVideo: false,
      isDraggingImage: false,
      imageFiles: [],
      videoFiles: [],
      fileUrls: [],
      imageFileIds: [],
      videoFileIds: [],

      categoryDetail: {},
      ogPropertiesReady: false,

      groups: {
        mode: 'tags',
        value: [],
        options: [],
        searchable: true,
        createTag: true,
      },
      formData: {
        category: null,
        // origin: 0,
        district: null,
        on_exchange: false,
        on_credit: false,
        description: '',
        allow_comments: false,
        price: 0,
        phone: '',
        images: [],
        values: [],
        select_values: [],
      },
      resetKey: 0,
    };
  },
  methods: {
    async onThumbnailChange(e) {
      const file = e.target.files?.[0];
      if (!file) return;

      // ✅ освободить старый preview
      if (this.thumbnailPreview) {
        URL.revokeObjectURL(this.thumbnailPreview);
      }

      this.thumbnailFile = file;
      this.thumbnailPreview = URL.createObjectURL(file);
      this.thumbnailId = null;

      try {
        this.loadingThumbnail = true;

        const fd = new FormData();
        fd.append('path', file);

        const res = await api.post('files/medias/', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (res && res.status === 201) {
          this.thumbnailId = res.data.id;

          // ✅ чтобы тот же файл можно было выбрать снова
          if (this.$refs.thumbnailInput) this.$refs.thumbnailInput.value = '';
        } else {
          this.$toast.warning(`${i18n.global.t('warning.UPLOAD_FAILED')}`);
          this.thumbnailPreview = null;
          this.thumbnailFile = null;
        }
      } catch (err) {
        console.log(err);
        this.$toast.warning(`${i18n.global.t('warning.UPLOAD_FAILED')}`);
        this.thumbnailPreview = null;
        this.thumbnailFile = null;
      } finally {
        this.loadingThumbnail = false;
      }
    },

    removeThumbnail() {
      if (this.thumbnailPreview) {
        URL.revokeObjectURL(this.thumbnailPreview);
      }

      this.thumbnailId = null;
      this.thumbnailFile = null;
      this.thumbnailPreview = null;

      if (this.$refs.thumbnailInput) {
        this.$refs.thumbnailInput.value = '';
      }
    },
    async validateDynamicFields() {
      var isValid = [];
      var fields = [];
      const state = { valid: true };

      for (let index = 0; index < this.childComponents.length; index++) {
        const element = this.childComponents[index];
        let x = await this.$refs[element][0].validateField();
        isValid.push(x.valid);
        fields.push(x);
      }
      if (isValid.includes(false)) {
        state['valid'] = false;
      }
      state['fields'] = fields;
      return state;
    },
    dragoverImage(e) {
      e.preventDefault();
      this.isDraggingImage = true;
    },
    dragleaveImage() {
      this.isDraggingImage = false;
    },
    async dropImage(e) {
      e.preventDefault();
      await this.onImageFileChange(e.dataTransfer.files);
      this.isDraggingImage = false;
    },
    removeImage(i) {
      const f = this.imageFiles[i];
      if (f?.path?.startsWith('blob:')) URL.revokeObjectURL(f.path);
      this.imageFiles.splice(i, 1);
      this.imageFileIds.splice(i, 1);
    },
    async onImageFileChange(eOrFiles) {
      const files =
        eOrFiles?.target?.files || // event
        eOrFiles || // FileList (drop)
        this.$refs.imageFile?.files ||
        [];

      if (!files || !files.length) return;

      const remaining = this.getMaxImageCount - this.imageFiles.length;

      if (remaining <= 0) {
        this.$toast.info(`${this.$t('page.maxImage')} (${this.getMaxImageCount})`);
        if (eOrFiles?.target && this.$refs.imageFile) this.$refs.imageFile.value = '';
        return;
      }

      const slice = Array.from(files).slice(0, remaining);

      if (slice.length < files.length) {
        this.$toast.info(`${this.$t('page.maxImage')} (${this.getMaxImageCount})`);
      }

      this.loadingImage = true;
      try {
        for (const f of slice) {
          const fd = new FormData();
          fd.append('path', f);

          const res = await api.post('files/medias/', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          if (res?.status === 201) {
            this.imageFiles.push({ detail: f, path: URL.createObjectURL(f), isUploaded: true });
            this.imageFileIds.push(res.data.id);
          } else {
            this.$toast.warning(i18n.global.t('warning.UPLOAD_FAILED'));
          }
        }
      } catch (e) {
        console.log(e);
        this.$toast.warning(i18n.global.t('warning.UPLOAD_FAILED'));
      } finally {
        this.loadingImage = false;
        if (eOrFiles?.target && this.$refs.imageFile) this.$refs.imageFile.value = '';
      }
    },
    dragoverVideo(e) {
      e.preventDefault();
      this.isDraggingVideo = true;
    },
    dragleaveVideo() {
      this.isDraggingVideo = false;
    },
    async dropVideo(e) {
      e.preventDefault();
      await this.onVideoFileChange(e.dataTransfer.files);
      this.isDraggingVideo = false;
    },
    removeVideo(i) {
      const f = this.videoFiles[i];
      if (f?.path?.startsWith('blob:')) URL.revokeObjectURL(f.path);
      this.videoFiles.splice(i, 1);
      this.videoFileIds.splice(i, 1);
    },
    async onVideoFileChange(eOrFiles) {
      const files =
        eOrFiles?.target?.files || // event
        eOrFiles || // FileList (drop)
        this.$refs.videoFile?.files ||
        [];

      if (!files || !files.length) return;

      const remaining = this.getMaxVideoCount - this.videoFiles.length;

      if (remaining <= 0) {
        this.$toast.info(`${this.$t('page.maxVideo')} (${this.getMaxVideoCount})`);
        if (eOrFiles?.target && this.$refs.videoFile) this.$refs.videoFile.value = '';
        return;
      }

      const slice = Array.from(files).slice(0, remaining);

      if (slice.length < files.length) {
        this.$toast.info(`${this.$t('page.maxVideo')} (${this.getMaxVideoCount})`);
      }

      this.loadingVideo = true;
      try {
        for (const f of slice) {
          const fd = new FormData();
          fd.append('path', f);

          const res = await api.post('files/medias/', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          if (res?.status === 201) {
            this.videoFiles.push({ detail: f, path: URL.createObjectURL(f), isUploaded: true });
            this.videoFileIds.push(res.data.id);
          } else {
            this.$toast.warning(i18n.global.t('warning.UPLOAD_FAILED'));
          }
        }
      } catch (e) {
        console.log(e);
        this.$toast.warning(i18n.global.t('warning.UPLOAD_FAILED'));
      } finally {
        this.loadingVideo = false;
        if (eOrFiles?.target && this.$refs.videoFile) this.$refs.videoFile.value = '';
      }
    },
    async getProperties() {
      this.loading = true;
      try {
        const response = await api.get(
          `attributes/properties/?publication_categories=${this.currentCategory}`,
        );
        this.ogProperties = response.data;

        await Promise.all(
          this.ogProperties.map(async element => {
            this.childComponents.push(element.id);
          }),
        );

        this.ogPropertiesReady = true;
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },
    async getSelectValues(property) {
      try {
        const response = await api.get(`attributes/select_values/?property=${property}`);
        return response.data;
      } catch (e) {
        console.log(e);
      }
    },

    async getCategoryDetail() {
      try {
        const response = await api.get(`publications/categories/${this.currentCategory}/`);
        this.categoryDetail = response.data;
      } catch (e) {
        console.log(e);
      }
    },

    async getPublicationLocations() {
      this.loading = true;
      try {
        const response = await api.get(`publications/locations/`);
        this.publicationLocations = response.data;

        this.publicationLocations.forEach(element => {
          if (element.data_type >= 4) {
            this.getDistricts(element.id).then(elem => {
              element['values'] = elem;
            });
          }
        });
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },
    async getDistricts(location) {
      this.loading = true;
      try {
        const response = await api.get(`publications/districts/?location=${location}`);
        return response.data;
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },
    clearAll() {
      this.resetKey += 1;
      // сброс thumbnail отдельно
      this.removeThumbnail();
      // освободить blob preview для картинок
      this.imageFiles.forEach(f => {
        if (f?.path?.startsWith('blob:')) URL.revokeObjectURL(f.path);
      });

      // освободить blob preview для видео
      this.videoFiles.forEach(f => {
        if (f?.path?.startsWith('blob:')) URL.revokeObjectURL(f.path);
      });
      // на всякий: очистим массивы айди/превью
      this.imageFiles = [];
      this.videoFiles = [];
      this.imageFileIds = [];
      this.videoFileIds = [];
    },

    async onSubmitForm() {
      // сброс (ничего лишнего не трогаем)
      this.formData.values = [];
      this.formData.select_values = [];

      let address, price, on_exchange, on_credit, title;
      const isValid = [];

      const dynamicFields = await this.validateDynamicFields();
      const terms = await this.termsValidate();

      const phone = await this.$refs.phone.validateField();
      const allow_comments = await this.$refs.allow_comments.validateField();

      if (this.categoryDetail?.has_address) {
        address = await this.$refs.address.validateField();
        isValid.push(address.valid);
      }
      if (this.categoryDetail?.has_name) {
        title = await this.$refs.title.validateField();
        isValid.push(title.valid);
      }
      if (this.categoryDetail?.has_price) {
        price = await this.$refs.price.validateField();
        isValid.push(price.valid);
      }
      if (this.categoryDetail?.on_exchange) {
        on_exchange = await this.$refs.on_exchange.validateField();
        isValid.push(on_exchange.valid);
      }
      if (this.categoryDetail?.on_credit) {
        on_credit = await this.$refs.on_credit.validateField();
        isValid.push(on_credit.valid);
      }

      isValid.push(terms.valid, phone.valid, allow_comments.valid);

      // ✅ THUMBNAIL REQUIRED — только если категория разрешает thumbnail picture И он required
      const thumbnailRequired =
        !!this.categoryDetail?.has_thumbnail_picture &&
        !!this.categoryDetail?.is_thumbnail_picture_required;

      if (thumbnailRequired && !this.thumbnailId) {
        this.$toast.error(i18n.global.t('warning.THUMBNAIL_REQUIRED'));
        return;
      }

      // основная валидация
      if (dynamicFields.valid && !isValid.includes(false)) {
        this.loading = true;

        // =========================
        // ✅ helpers (как ты просил)
        // =========================
        const normalizeSingle = v => {
          if (v && typeof v === 'object' && 'value' in v) v = v.value;
          if (v === null || v === undefined) return null;
          const s = String(v).trim();
          return s.length ? s : null;
        };

        const normalizeMulti = v => {
          if (v === null || v === undefined) return [];
          let arr = Array.isArray(v) ? v : [v];
          arr = arr
            .map(x => (x && typeof x === 'object' ? (x.id ?? x.value) : x))
            .map(x => Number(x))
            .filter(x => Number.isFinite(x));
          return Array.from(new Set(arr));
        };

        // =========================
        // ✅ сбор dynamic fields
        // =========================
        const fields = dynamicFields.fields || [];

        for (let i = 0; i < fields.length; i++) {
          const el = fields[i];

          // single -> values
          if (el.mode === 'single') {
            const name = normalizeSingle(el.value);
            const propertyId = Number(el.property);

            if (!name) continue;
            if (!Number.isFinite(propertyId) || propertyId <= 0) continue;

            this.formData.values.push({ name, property: propertyId });
            continue;
          }

          // multi -> select_values
          const ids = normalizeMulti(el.value);
          if (ids.length) this.formData.select_values.push(...ids);
        }

        // ✅ финальная чистка
        this.formData.values = (this.formData.values || []).filter(
          v =>
            v &&
            typeof v.name === 'string' &&
            v.name.trim().length > 0 &&
            Number.isFinite(Number(v.property)),
        );

        this.formData.select_values = Array.from(
          new Set((this.formData.select_values || []).map(Number).filter(Number.isFinite)),
        );

        try {
          // обычные поля
          this.formData.phone = phone.value;
          this.formData.allow_comments = allow_comments.value;

          if (this.categoryDetail?.has_name) this.formData.name = title.value;
          if (this.categoryDetail?.has_price) this.formData.price = price.value;

          // address может иметь разный формат — не ломаем, но защищаемся от undefined
          if (this.categoryDetail?.has_address) {
            const districtId = Array.isArray(address?.value) ? address.value[1] : null;
            this.formData.district = districtId;
          }

          if (this.categoryDetail?.on_exchange) this.formData.on_exchange = on_exchange.value;
          if (this.categoryDetail?.on_credit) this.formData.on_credit = on_credit.value;

          // ✅ images — только фото/видео (thumbnail сюда НЕ кладём)
          this.formData.images = [];
          if (this.categoryDetail?.has_image) {
            this.formData.images = [...this.imageFileIds];
          }
          this.formData.images = [...this.formData.images, ...this.videoFileIds];

          // ✅ thumbnail_picture — отдельно (не в images)
          this.formData.thumbnail_picture = this.categoryDetail?.has_thumbnail_picture
            ? this.thumbnailId
            : null;

          // категория (как было, ничего лишнего)
          this.formData.category = this.currentCategory;

          // ✅ быстрый тест (временно)
          console.log('PAYLOAD values:', this.formData.values);
          console.log('PAYLOAD select_values:', this.formData.select_values);

          const response = await api.post(`publications/my-publication-requests/`, this.formData);

          this.$toast.info(`${i18n.global.t('warning.PUBLICATION_ADDED')}`);
          this.$router.push({ name: 'publicationRequests' });
        } catch (e) {
          console.log(e);
        } finally {
          this.loading = false;
        }
      } else {
        this.$toast.error(`${i18n.global.t('warning.PUBLICATION_FIELDS_EMPTY')}`);
      }
    },

    getComponent(type) {
      switch (type) {
        case 0:
          return TextField;
        case 1:
          return NumberField;
        case 2:
          return NumberField;
        case 3:
          return SwitchField;
        case 4:
          return SelectField;
        case 5:
          return MultiSelectField;
        case 6:
          return NestedSelectField;
        case 7:
          return MultiNestedSelectField;
        case 8:
          return NestedMultiSelectField;
        default:
          return TextField; // Default to text input component
      }
    },
  },
  computed: {
    getMaxImageCount() {
      return this.$store.setting.max_publication_image_count;
    },
    getMaxVideoCount() {
      return this.$store.setting.max_publication_video_count;
    },
  },
  created() {
    this.currentCategory = this.$route.params.id;
    this.getCategoryDetail();
    this.getProperties();
    this.getPublicationLocations();
  },
};
</script>

<style>
@import '@vueform/multiselect/themes/tailwind.css';
</style>
